import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  LoginSkeleton,
  DashboardSkeleton,
  AttendanceSkeleton,
  MarksSkeleton,
  AdminDashboardSkeleton,
  GenericPageSkeleton,
  AppSkeleton,
} from './SkeletonLoader';

/**
 * Minimum time (ms) the skeleton must be shown before content is revealed.
 * This ensures fonts are loaded and a smooth visual transition occurs.
 */
const MIN_SKELETON_MS = 2000;

/**
 * Map route patterns to their specific skeleton variants.
 */
function getSkeletonForPath(pathname) {
  if (pathname === '/login' || pathname === '/') return <LoginSkeleton />;
  if (pathname.includes('/teacher/dashboard') || pathname === '/dashboard') return <DashboardSkeleton />;
  if (pathname.includes('/teacher/attendance') || pathname === '/attendance') return <AttendanceSkeleton />;
  if (pathname.includes('/teacher/register') || pathname === '/marks') return <MarksSkeleton />;
  if (pathname === '/admin/dashboard') return <AdminDashboardSkeleton />;
  if (pathname.startsWith('/admin/')) return <GenericPageSkeleton />;
  if (pathname === '/sync-hub') return <GenericPageSkeleton />;
  return <AppSkeleton />;
}

/**
 * AppWithSkeleton — Wraps the entire App and enforces a skeleton loader
 * on every route navigation. The skeleton is shown for a minimum of 2 seconds
 * (MIN_SKELETON_MS) or longer if the page isn't ready yet.
 *
 * This ensures:
 * 1. Fonts (Gowun Batang, Krub) have time to load
 * 2. Data fetches from IndexedDB can complete
 * 3. A consistent, polished loading UX
 */
export function AppWithSkeleton({ children }) {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [currentPath, setCurrentPath] = useState(location.pathname);

  // Track navigation changes
  useEffect(() => {
    // Only re-trigger skeleton if the route actually changed
    if (location.pathname !== currentPath) {
      setCurrentPath(location.pathname);
      setIsLoading(true);
    }
  }, [location.pathname, currentPath]);

  // Enforce minimum skeleton display time
  useEffect(() => {
    if (!isLoading) return;

    const timer = setTimeout(() => {
      setIsLoading(false);
    }, MIN_SKELETON_MS);

    return () => clearTimeout(timer);
  }, [isLoading, currentPath]);

  if (isLoading) {
    return (
      <div className="min-h-screen" aria-busy="true" aria-live="polite">
        {getSkeletonForPath(currentPath)}
      </div>
    );
  }

  return (
    <div className="content-loaded" aria-busy="false">
      {children}
    </div>
  );
}
